import { type FC, type ReactNode } from "react";

import { Box, Group, Skeleton, Stack, Text, UnstyledButton, VisuallyHidden } from "@mantine/core";
import { Colors, Tooltip } from "@snapchat/snap-design-system";
import { InformationIcon } from "@snapchat/snap-design-system-icons";

import { MetricChange } from "./MetricChange";
import { MetricDiff } from "./MetricDiff";
import { cardContainer, heroValue, hiddenComparison, infoIcon, label, normalValue } from "./StatsCard.module.css";

export interface StatsCardProps {
    label: ReactNode;
    tooltip: ReactNode;
    value: string;
    diffValue: string | null;
    diffText: ReactNode;
    size?: "normal" | "hero";
    reserveDiffSpace?: boolean;
    loading?: boolean;
}

export const StatsCard: FC<StatsCardProps> = ({
    label: labelText,
    tooltip,
    value: valueText,
    diffValue,
    diffText: diffTextContent,
    size = "hero",
    reserveDiffSpace = false,
    loading = false,
}) => {
    const valueClassName = size === "normal" ? normalValue : heroValue;

    return (
        <Box flex={1} px="lg" py="md" className={cardContainer} aria-busy={loading}>
            <Stack gap={4}>
                <Group gap={8} className={label}>
                    {labelText}
                    <Tooltip content={tooltip}>
                        <UnstyledButton>
                            <VisuallyHidden>{labelText}</VisuallyHidden>
                            <InformationIcon
                                size={12}
                                className={infoIcon}
                                fill={Colors.Black.V50}
                                aria-hidden="true"
                            />
                        </UnstyledButton>
                    </Tooltip>
                </Group>
                {loading ? (
                    <Skeleton h={size === "normal" ? 24 : 48} w={100} aria-hidden="true" />
                ) : (
                    <Text className={valueClassName}>{valueText}</Text>
                )}
                {(diffValue !== null || reserveDiffSpace) && (
                    <Box className={diffValue === null ? hiddenComparison : undefined}>
                        <MetricChange
                            badge={<MetricDiff diffValue={diffValue ?? "N/A"} />}
                            text={diffTextContent}
                            wrapText={reserveDiffSpace}
                            layout={reserveDiffSpace ? "column" : "row"}
                        />
                    </Box>
                )}
            </Stack>
        </Box>
    );
};
